'use client'

import { useEffect, useRef, useState, useId } from 'react'
import { gsap } from 'gsap'
import { useTranslateEntry } from 'src/i18n/client/useTranslateEntry'
import Typography from 'src/components/design-system/Typography'
import { CustomSelect } from 'src/components/design-system/form/CustomSelect'
import { copyTextToClipboard } from 'src/_helpers/copyToClipboard'
import {
  articleCitationGenerators,
  bookCitationGenerators,
  chapterCitationGenerators,
  CITATION_STYLES,
} from 'src/components/publications/citationGenerator'
import Copy from 'public/icons/interface/copy.svg'
import Cite from 'public/icons/interface/cite.svg'
import Close from 'public/icons/interface/close.svg'

const STYLE_LABELS = {
  apa: 'APA',
  mla: 'MLA',
  chicago: 'Chicago',
  harvard: 'Harvard',
  vancouver: 'Vancouver',
  ama: 'AMA',
}

const citationStyles = CITATION_STYLES.map((style) => ({ value: style, label: STYLE_LABELS[style] || style }))

// Typ zamiast samych generatorów — komponenty serwerowe nie mogą przekazać funkcji do klienckiego.
const GENERATORS_BY_TYPE = {
  article: articleCitationGenerators,
  chapter: chapterCitationGenerators,
  book: bookCitationGenerators,
}

export default function Citation({ publication, locale, type = 'article' }) {
  const generators = GENERATORS_BY_TYPE[type] || articleCitationGenerators
  const [open, setOpen] = useState(false)
  const [selectedStyle, setSelectedStyle] = useState('apa')
  const [copySuccess, setCopySuccess] = useState(false)
  const [position, setPosition] = useState('left')
  const menuRef = useRef(null)
  const buttonRef = useRef(null)
  const menuId = useId()
  const translate = useTranslateEntry()

  const toggleMenu = () => {
    if (!open && buttonRef.current) {
      const rect = buttonRef.current.getBoundingClientRect()
      const menuWidth = 503
      const spaceRight = window.innerWidth - rect.right
      const spaceLeft = rect.left

      if (spaceRight < menuWidth && spaceLeft > spaceRight) {
        setPosition('right')
      } else {
        setPosition('left')
      }
    }
    setOpen((prev) => !prev)
  }

  useEffect(() => {
    if (!menuRef.current) return
    if (open) gsap.fromTo(menuRef.current, { opacity: 0 }, { opacity: 1, duration: 0.3 })
    else gsap.to(menuRef.current, { opacity: 0, duration: 0.3 })
  }, [open])

  useEffect(() => {
    if (!open) {
      setCopySuccess(false)
    }
  }, [open])

  const handleCopyToClipboard = async () => {
    const citation = generators.citation(publication, selectedStyle, locale)
    const plainText = citation.replace(/<\/?i>/g, '')
    const success = await copyTextToClipboard(plainText)
    if (success) {
      setCopySuccess(true)
      setTimeout(() => setCopySuccess(false), 2000)
    }
  }

  const handleDownload = (format) => {
    let content = ''
    let filename = ''
    let mimeType = 'text/plain'

    if (format === 'bibtex') {
      content = generators.bibtex(publication, locale)
      filename = 'citation.bib'
      mimeType = 'application/x-bibtex'
    } else if (format === 'ris') {
      content = generators.ris(publication, locale)
      filename = 'citation.ris'
      mimeType = 'application/x-research-info-systems'
    }

    const blob = new Blob([content], { type: mimeType })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = filename
    document.body.appendChild(a)
    a.click()
    document.body.removeChild(a)
    URL.revokeObjectURL(url)
  }

  const citation = generators.citation(publication, selectedStyle, locale)

  return (
    <div className="relative inline-block text-left">
      <button
        ref={buttonRef}
        onClick={toggleMenu}
        aria-controls={menuId}
        aria-expanded={open}
        className="flex items-center gap-x-4"
      >
        <Cite />
        <Typography variant="h3-bold" component="span" color="text-basic-black">
          {translate('T_CITE_ARTICLE')}
        </Typography>
      </button>

      {open && (
        <div
          id={menuId}
          ref={menuRef}
          role="menu"
          className={`fixed t:absolute left-1/2 -translate-x-1/2 t:translate-x-0 t:left-auto ${position === 'right' ? 't:right-0' : 't:left-0'} top-1/2 -translate-y-1/2 t:translate-y-0 t:top-auto mt-0 t:mt-9 p-24 w-[90vw] t:w-[503px] bg-basic-white rounded-4 flex flex-col gap-y-24 shadow-[0px_4px_14px_0px_#0000001A] opacity-0 z-50`}
        >
          <div className="flex justify-between items-center">
            <Typography variant="h3-bold" component="h2" color="text-basic-black">
              {translate('T_CITE_ARTICLE')}
            </Typography>
            <button onClick={toggleMenu} aria-label={translate('T_CLOSE')}>
              <Close />
            </button>
          </div>
          <hr className="w-full h-1 block bg-basic-gray10 text-basic-gray10" />
          <div className="flex gap-x-11 items-end">
            <CustomSelect
              label={translate('T_CITATION_STYLE')}
              name="citationStyle"
              options={citationStyles}
              defaultValue={selectedStyle}
              onChange={(e) => {
                setSelectedStyle(e.target.value)
                setCopySuccess(false)
              }}
              color="dark-blue"
              className="cursor-pointer"
              key={selectedStyle}
            />
            <button onClick={handleCopyToClipboard} className="h-44 flex items-center gap-x-4 group cursor-pointer">
              <Copy className="min-w-24" />
              <Typography
                variant="h3-bold"
                component="span"
                color="text-basic-black"
                className={`w-fit h-fit relative after:content-[''] after:h-1 after:w-0 after:transition-[width] after:absolute after:bottom-0 after:left-0 group-hover:after:w-full after:bg-basic-black`}
              >
                {copySuccess ? translate('T_COPIED') : translate('T_COPY_TO_CLIPBOARD')}
              </Typography>
            </button>
          </div>

          <Typography
            variant="body3"
            color="text-basic-black"
            className="text-block-content"
            dangerouslySetInnerHTML={{ __html: citation }}
          />
          <hr className="w-full h-1 block bg-basic-gray10 text-basic-gray10" />
          <Typography variant="h3-bold" component="p" color="text-basic-black">
            {translate('T_DOWNLOAD')}
          </Typography>
          <div className="flex gap-x-24">
            <button onClick={() => handleDownload('bibtex')} className="group cursor-pointer">
              <Typography
                variant="button-bold"
                component="span"
                color="text-dark-blue-dark-blue2"
                className={`w-fit h-fit relative after:content-[''] after:h-1 after:w-full after:transition-[width] after:absolute after:bottom-0 after:left-0 group-hover:after:w-0 after:bg-dark-blue-dark-blue2`}
              >
                {translate('T_DOWNLOAD_BIBTEX')}
              </Typography>
            </button>

            <button onClick={() => handleDownload('ris')} className="group cursor-pointer">
              <Typography
                variant="button-bold"
                component="span"
                color="text-dark-blue-dark-blue2"
                className={`w-fit h-fit relative after:content-[''] after:h-1 after:w-full after:transition-[width] after:absolute after:bottom-0 after:left-0 group-hover:after:w-0 after:bg-dark-blue-dark-blue2`}
              >
                {translate('T_DOWNLOAD_RIS')}
              </Typography>
            </button>
          </div>
        </div>
      )}
    </div>
  )
}
